/**
 * What the programme has already produced: its snapshots and its exports.
 *
 * The governance surface records them as it locks them; the submission pack is where
 * somebody assembling a validation pack goes to see what exists.
 *
 * @module components/submission/ArtefactList
 */

import React from "react";

import { userDisplayName } from "@/components/shared/user-display-name";
import {
  Badge,
  CollectionView,
  type Column,
  EmptyState,
  MutedText,
  Panel,
  type RowActionsSpec,
  TextLink,
  Time,
} from "@/components/ui";
import type { ExportRecord, Snapshot } from "@/server/types";

const FORMAT_LABEL: Readonly<Record<string, string>> = {
  descriptor: "Programme descriptor",
  schedule: "Programme schedule",
  json: "Governed JSON",
};

export interface ArtefactListProps {
  exports: readonly ExportRecord[];
  snapshots: readonly Snapshot[];
  /** How a user id reads. Absent names fall back to the id. */
  nameFor?: (userId: string) => string | undefined;
  /** Hand an export's file over again, rebuilt from the snapshot it locked. */
  onDownload?: (row: ExportRecord, snapshot: Snapshot) => void;
  /** The open draft's save revision, which a preview must match to be rebuilt. */
  currentRevision?: string;
  /** Build a preview's file again from the open draft. */
  onRegenerate?: (row: ExportRecord) => void;
  /** Bring the page's Generate pack button into view, from the empty state. */
  onShowGeneratePack?: () => void;
}

const NOT_DOWNLOADABLE = "Only an export taken from a locked snapshot can be downloaded again.";
const PREVIEW_STALE =
  "The draft has changed since this preview. Export it again to download the current draft.";

/** The latest snapshot by its own date, not by where the read put it. */
function newest(snapshots: readonly Snapshot[]): Snapshot {
  return snapshots.reduce((latest, next) => (next.createdAt > latest.createdAt ? next : latest));
}

export function ArtefactList({
  exports,
  snapshots,
  nameFor = undefined,
  onDownload = undefined,
  currentRevision = undefined,
  onRegenerate = undefined,
  onShowGeneratePack = undefined,
}: ArtefactListProps): React.ReactElement {
  // A preview keeps no file; the draft it came from rebuilds it while unchanged.
  const rebuildable = (row: ExportRecord): boolean =>
    row.preview === true &&
    row.format === "descriptor" &&
    onRegenerate !== undefined &&
    row.revisionId === currentRevision;
  const snapshotFor = (row: ExportRecord): Snapshot | undefined =>
    snapshots.find((candidate) => candidate.id === row.snapshotId);
  const rowActions: RowActionsSpec<ExportRecord> | undefined = onDownload
    ? {
        label: (row) => FORMAT_LABEL[row.format] ?? row.format,
        testId: (row) => `artefact-actions-${row.id}`,
        items: (row) => {
          const locked = snapshotFor(row);
          const rebuild = rebuildable(row);
          const base = {
            key: "download",
            label: `Download ${FORMAT_LABEL[row.format] ?? row.format}`,
            icon: "download-simple",
            primary: true,
            onSelect: () => {
              if (locked) {
                onDownload(row, locked);
              } else {
                onRegenerate?.(row);
              }
            },
            "data-testid": `artefact-download-${row.id}`,
          };
          return [
            locked || rebuild
              ? base
              : {
                  ...base,
                  disabled: true as const,
                  disabledReason: row.preview ? PREVIEW_STALE : NOT_DOWNLOADABLE,
                },
          ];
        },
      }
    : undefined;
  const columns: readonly Column<ExportRecord>[] = [
    {
      key: "format",
      header: "Artefact",
      render: (row) => FORMAT_LABEL[row.format] ?? row.format,
      sortValue: (row) => row.format,
    },
    {
      key: "state",
      header: "State",
      render: (row) => (
        <Badge tone={row.preview ? "warning" : "success"} variant="soft">
          {row.preview ? "Preview" : "Governed"}
        </Badge>
      ),
      sortValue: (row) => (row.preview ? "preview" : "governed"),
    },
    {
      key: "programmeState",
      header: "Programme state",
      render: (row) => row.workflowState ?? "Not recorded",
      sortValue: (row) => row.workflowState ?? "",
      priority: 2,
    },
    {
      key: "template",
      header: "Template",
      render: (row) => row.template ?? "—",
      priority: 3,
    },
    {
      key: "actor",
      header: "Exported by",
      render: (row) => nameFor?.(row.actor) ?? userDisplayName(row.actor, {}),
      priority: 2,
    },
    {
      key: "timestamp",
      header: "Exported",
      render: (row) => <Time value={row.timestamp} />,
      sortValue: (row) => row.timestamp,
    },
  ];

  return (
    <Panel
      title="Artefacts"
      icon="files"
      description="View previews, exports and locked snapshots of this programme."
      data-testid="submission-artefacts"
    >
      <CollectionView
        storageKey="submission-artefact-table"
        columns={columns}
        rowActions={rowActions}
        rows={[...exports].sort((a, b) => b.timestamp.localeCompare(a.timestamp))}
        rowKey={(row) => row.id}
        rowTestId={(row) => `artefact-${row.id}`}
        caption="Artefacts, newest first"
        showCaption
        data-testid="submission-artefact-table"
        // The final export writes its snapshot and its export together, so a
        // snapshot never stands without an export row.
        emptyState={
          <EmptyState
            icon="file-dashed"
            title="No artefacts yet"
            description={
              <>
                A preview is listed each time a Word descriptor or schedule is generated from{" "}
                {onShowGeneratePack ? (
                  <TextLink
                    href="#submission-generate-pack"
                    onClick={(event) => {
                      event.preventDefault();
                      onShowGeneratePack();
                    }}
                    data-testid="artefacts-generate-pack-link"
                  >
                    Generate pack
                  </TextLink>
                ) : (
                  "Generate pack"
                )}
                . The governed export is listed when the Programme Lead runs the final export, which
                needs the programme Final Descriptor Signed Off (Programme Lead and QA sign-offs
                recorded) and readiness at Ready for final export.
              </>
            }
          />
        }
      />
      {snapshots.length > 0 ? (
        <MutedText className="mt-3">
          The most recent snapshot was taken <Time value={newest(snapshots).createdAt} />.
        </MutedText>
      ) : null}
    </Panel>
  );
}

export default ArtefactList;
